import { createFileRoute, Link } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { MarketingShell, MarketingHero } from "@/components/MarketingShell";
import { PageContainer } from "@/components/Logo";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/about")({
  component: AboutPage,
  head: () => ({
    meta: [
      { title: "About MissionReady — Built for private security operations" },
      {
        name: "description",
        content:
          "MissionReady is the field-operations platform for security companies, patrol services, and in-house teams — replacing clipboards, group texts, and screenshots.",
      },
      ...socialMeta({
        title: "About MissionReady",
        description: "MissionReady is the field-operations platform for security companies, patrol services, and in-house teams — replacing clipboards, group texts, and screenshots.",
        url: "https://missionready.dev/about",
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/about" }],
    scripts: [
      ...ORG_WEBSITE_JSONLD,
    ],
  }),
});

const WHO = [
  {
    title: "Private security companies",
    desc: "Contract guarding firms running multiple sites, clients, and shift rosters that need proof of service on every invoice.",
  },
  {
    title: "Mobile patrol services",
    desc: "Vehicle and foot patrol operators who need GPS breadcrumbs, checkpoint scans, and time-stamped visit records.",
  },
  {
    title: "In-house field ops teams",
    desc: "Corporate, campus, and property security departments consolidating dispatch, incidents, and compliance in one place.",
  },
];

const REPLACES = [
  ["Paper logs and clipboards", "Digital patrols with GPS + signed QR checkpoints"],
  ["Group texts and phone trees", "Live dispatch with SOS and acknowledgements"],
  ["Photos in personal camera rolls", "Structured incident reports with attachments and signatures"],
  ["Weekly PDF email updates", "White-labeled client portal with live activity"],
  ["Spreadsheet rosters", "Scheduling with open shifts, swaps, and coverage alerts"],
  ["Expired license surprises", "Credential tracking with expiry reminders"],
];

function AboutPage() {
  return (
    <MarketingShell>
      <MarketingHero
        eyebrow="ABOUT MISSIONREADY"
        title={<>Accountability you can hand to a client.</>}
        subtitle="MissionReady is the operations platform for private security teams — dispatch, GPS, patrols, incidents, and client-ready reporting from one command center."
      />

      <section className="py-16 md:py-20">
        <PageContainer>
          <h2 className="font-display text-2xl font-bold md:text-3xl">Who it's for</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WHO.map((w) => (
              <div key={w.title} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-lg font-semibold">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.desc}</p>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      <section className="border-t border-border/60 py-16 md:py-20">
        <PageContainer>
          <h2 className="font-display text-2xl font-bold md:text-3xl">What it replaces</h2>
          <ul className="mt-6 divide-y divide-border/60 overflow-hidden rounded-2xl border border-border bg-card">
            {REPLACES.map(([before, after]) => (
              <li key={before} className="grid gap-1 p-4 sm:grid-cols-2 sm:gap-6 sm:p-5">
                <span className="text-sm text-muted-foreground line-through decoration-primary/50">
                  {before}
                </span>
                <span className="text-sm font-medium text-foreground">{after}</span>
              </li>
            ))}
          </ul>
        </PageContainer>
      </section>

      <section className="border-t border-border/60 py-16 md:py-20">
        <PageContainer className="max-w-3xl">
          <h2 className="font-display text-2xl font-bold md:text-3xl">Why we built it</h2>
          <div className="mt-5 space-y-4 text-sm leading-relaxed text-muted-foreground md:text-base">
            <p>
              MissionReady started from a simple, frustrating gap in the field: the work
              gets done, but nobody can prove it. Officers walk the site, handle the
              incident, and lock up — then the record of it lives in a text thread, a
              paper log, or someone's camera roll. When a client asks what happened at
              02:40, the answer is a guess.
            </p>
            <p>
              We built the platform we wanted on shift: a single system of record where
              patrols, checkpoints, incidents, and dispatch all leave a time-stamped,
              tamper-evident trail. Officers get one app that works offline. Supervisors
              get live visibility. Clients get reporting that speaks for itself.
            </p>
            <p>
              Every organization is isolated at the database level, roles are enforced
              server-side, and audit trails are on by default — because accountability
              that can be edited quietly isn't accountability.
            </p>
          </div>

          <div className="mt-8">
            <Button asChild size="lg">
              <Link to="/signup">Start free — create your organization</Link>
            </Button>
            <p className="mt-3 text-xs text-muted-foreground">
              30-day free trial on paid plans · No credit card · Free forever for solo officers
            </p>
          </div>
        </PageContainer>
      </section>

      <section className="border-t border-border/60 py-10">
        <PageContainer>
          <h2 className="font-display text-sm font-semibold uppercase tracking-[0.18em]">
            Company &amp; legal
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            MissionReady · United States · Reach us any time at{" "}
            <Link to="/contact" className="underline hover:text-foreground">
              our contact page
            </Link>
            .
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            <Link to="/privacy-policy" className="underline hover:text-foreground">
              Privacy policy
            </Link>{" "}
            ·{" "}
            <Link to="/terms" className="underline hover:text-foreground">
              Terms of service
            </Link>
          </p>
        </PageContainer>
      </section>
    </MarketingShell>
  );
}
